import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FrenchDatePicker from "./FrenchDatePicker";
import Input from "../ui/Input";
import Button from "../ui/Button";

const EffectiveIndirect = ({
  getIndirect = "effective_indirect_standard",
  url = "effective_standard",
}) => {
  const readOnly = localStorage.getItem("role") === "Gerant";
  const [data, setData] = useState({
    cointa: "",
    price_by_part: "",
    mag_four: "",
    mag_fin: "",
    autres: "",
    autres_detail: "",
    machines_sp_manuelle: "",
    trassage_special: "",
    cont_fin: "",
    mach_retouche: "",
    repassage: "",
    gabaret: "",
    preparation_stagieres: "",
    preparation: "",
    matlasseurs: "",
    coupeurs: "",
    tiquitage: "",
    vesline: "",
    custom_date: new Date(), // Start with current date
  });

  const [loading, setLoading] = useState(false);
  const [effectiveStandardId, setEffectiveStandardId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false); // Flag to prevent infinite loops

  const onChange = (name, value) => {
    // Convert numeric fields to numbers
    const numericFields = [
      "mag_four",
      "mag_fin", 
      "autres",
      "machines_sp_manuelle",
      "trassage_special",
      "cont_fin",
      "mach_retouche",
      "repassage",
      "gabaret",
      "preparation_stagieres",
      "preparation",
      "matlasseurs",
      "coupeurs",
      "tiquitage",
      "vesline",
      "price_by_part",
    ];
  
    const newValue = numericFields.includes(name)
      ? Number(value) || ""  
      : value;
  
    setData({
      ...data,
      [name]: newValue,
    });
  };

  const calculateTotal = (effectiveStandardData) => {
    const sum =
      (parseFloat(effectiveStandardData.mag_four) || 0) +
      (parseFloat(effectiveStandardData.mag_fin) || 0) +
      (parseFloat(effectiveStandardData.autres) || 0) +
      (parseFloat(effectiveStandardData.machines_sp_manuelle) || 0) +
      (parseFloat(effectiveStandardData.trassage_special) || 0) +
      (parseFloat(effectiveStandardData.cont_fin) || 0) +
      (parseFloat(effectiveStandardData.mach_retouche) || 0) +
      (parseFloat(effectiveStandardData.repassage) || 0) +
      (parseFloat(effectiveStandardData.gabaret) || 0) +
      (parseFloat(effectiveStandardData.preparation_stagieres) || 0) +
      (parseFloat(effectiveStandardData.preparation) || 0) +
      (parseFloat(effectiveStandardData.coupes?.[0]?.matlasseurs) || 0) +
      (parseFloat(effectiveStandardData.coupes?.[0]?.coupeurs) || 0) +
      (parseFloat(effectiveStandardData.coupes?.[0]?.tiquitage) || 0) +
      (parseFloat(effectiveStandardData.coupes?.[0]?.vesline) || 0);
    setTotal(sum);
  };

  // Function to fetch data by date
  const fetchDataByDate = async (useData = null) => {
    const currentData = useData || data;
    
    if (isFetching) {
      console.log("fetchDataByDate: Already fetching, skipping");
      return;
    }

    if (!currentData.custom_date) {
      console.log("fetchDataByDate: No custom_date, setting isLoading to false");
      setIsLoading(false); // Ensure loading is set to false if no date
      return;
    }

    console.log("fetchDataByDate: Starting fetch for date:", currentData.custom_date);
    setIsFetching(true);

    try {
      const dateStr = currentData.custom_date.toISOString().split("T")[0];
      
      // For effective_real, fetch by date
      if (url === "effective_real") {
        console.log("fetchDataByDate: Fetching for effective_real mode");
        const response = await api.get(`/effective_indirect_real_by_date`, {
          params: { date: dateStr }
        });

        if (response.data && response.data.effectifIndirects && response.data.effectifIndirects.length > 0) {
          // Data exists - prefill the form
          console.log("fetchDataByDate: Data found, prefilling form");
          const effectifIndirect = response.data.effectifIndirects[0];
          
          setData(prevData => {
            const updatedData = {
              ...prevData,
              cointa: effectifIndirect.cointa || "",
              price_by_part: effectifIndirect.price_by_part || "",
              mag_four: effectifIndirect.mag_four || "",
              mag_fin: effectifIndirect.mag_fin || "",
              autres: effectifIndirect.autres || "",
              autres_detail: effectifIndirect.autres_detail || "",
              machines_sp_manuelle: effectifIndirect.machines_sp_manuelle || "",
              trassage_special: effectifIndirect.trassage_special || "",
              cont_fin: effectifIndirect.cont_fin || "",
              mach_retouche: effectifIndirect.mach_retouche || "",
              repassage: effectifIndirect.repassage || "",
              gabaret: effectifIndirect.gabaret || "",
              preparation_stagieres: effectifIndirect.preparation_stagieres || "",
              preparation: effectifIndirect.preparation || "",
              matlasseurs: effectifIndirect.coupes?.[0]?.matlasseurs || "",
              coupeurs: effectifIndirect.coupes?.[0]?.coupeurs || "",
              tiquitage: effectifIndirect.coupes?.[0]?.tiquitage || "",
              vesline: effectifIndirect.coupes?.[0]?.vesline || "",
            };
            
            return updatedData;
          });
          
          calculateTotal(effectifIndirect);
          setEffectiveStandardId(response.data.id); // Set ID for update
          toast.success(`Données existantes récupérées pour le ${dateStr}`);
        } else {
          // No data exists - clear form fields but keep date
          console.log("fetchDataByDate: No data found, clearing form");
          clearFormFields();
          setEffectiveStandardId(null); // Clear ID for insert
          toast.info(`Aucune donnée trouvée pour le ${dateStr} - Prêt pour nouvelle saisie`);
        }
      } else {
        // For effective_standard, use the existing logic
        console.log("fetchDataByDate: Fetching for effective_standard mode");
        const response = await api.get(getIndirect);
        
        if (response.data && response.status === 200) {
          const effectiveStandardData = response.data;
          
          if (effectiveStandardData && typeof effectiveStandardData === 'object' && !effectiveStandardData.message) {
            setData({
              custom_date: dateStr,
              cointa: effectiveStandardData.cointa || "",
              price_by_part: effectiveStandardData.price_by_part || "",
              mag_four: effectiveStandardData.mag_four || "",
              mag_fin: effectiveStandardData.mag_fin || "",
              autres: effectiveStandardData.autres || "",
              autres_detail: "",
              machines_sp_manuelle: effectiveStandardData.machines_sp_manuelle || "",
              trassage_special: effectiveStandardData.trassage_special || "",
              cont_fin: effectiveStandardData.cont_fin || "",
              mach_retouche: effectiveStandardData.mach_retouche || "",
              repassage: effectiveStandardData.repassage || "",
              gabaret: effectiveStandardData.gabaret || "",
              preparation_stagieres: effectiveStandardData.preparation_stagieres || "",
              preparation: effectiveStandardData.preparation || "",
              matlasseurs: effectiveStandardData.coupes?.[0]?.matlasseurs || "",
              coupeurs: effectiveStandardData.coupes?.[0]?.coupeurs || "",
              tiquitage: effectiveStandardData.coupes?.[0]?.tiquitage || "",
              vesline: effectiveStandardData.coupes?.[0]?.vesline || "",
            });

            setEffectiveStandardId(effectiveStandardData.id);
            calculateTotal(effectiveStandardData);
          } else {
            clearFormFields();
          }
        }
      }
    } catch (error) {
      console.error("fetchDataByDate: Error fetching data:", error);
      // On error, clear form but keep date
      clearFormFields();
      setEffectiveStandardId(null);
      toast.error("Erreur lors de la récupération des données.");
    } finally {
      console.log("fetchDataByDate: Fetch completed, setting isFetching to false");
      setIsFetching(false);
      // Always set isLoading to false for effective_real mode
      if (url === "effective_real") {
        console.log("fetchDataByDate: Setting isLoading to false for effective_real mode");
        setIsLoading(false);
      }
    }
  };

  // Function to clear form fields but preserve the date
  const clearFormFields = () => {
    setData(prevData => {
      const clearedData = {
        ...prevData,
        // Keep the custom_date
        custom_date: prevData.custom_date,
        // Clear only the effectif fields
        cointa: "",
        price_by_part: "",
        mag_four: "",
        mag_fin: "",
        autres: "",
        autres_detail: "",
        machines_sp_manuelle: "",
        trassage_special: "",
        cont_fin: "",
        mach_retouche: "",
        repassage: "",
        gabaret: "",
        preparation_stagieres: "",
        preparation: "",
        matlasseurs: "",
        coupeurs: "",
        tiquitage: "",
        vesline: "",
      };
      
      return clearedData;
    });
    setTotal(0);
  };

  // Function to clear entire form including date (for starting fresh)
  const clearEntireForm = () => {
    setData(prevData => {
      const clearedData = {
        ...prevData,
        // Clear everything except basic structure
        custom_date: new Date(), // Reset to current date
        cointa: "",
        price_by_part: "",
        mag_four: "",
        mag_fin: "",
        autres: "",
        autres_detail: "",
        machines_sp_manuelle: "",
        trassage_special: "",
        cont_fin: "",
        mach_retouche: "",
        repassage: "",
        gabaret: "",
        preparation_stagieres: "",
        preparation: "",
        matlasseurs: "",
        coupeurs: "",
        tiquitage: "",
        vesline: "",
      };
      
      return clearedData;
    });
    setTotal(0);
    setEffectiveStandardId(null);
  };

  useEffect(() => {
    console.log("useEffect: Component mounted or url changed, url:", url);
    console.log("useEffect: Current isLoading state:", isLoading);
    
    // Initial data fetch
    if (url === "effective_real") {
      console.log("useEffect: effective_real mode, calling fetchDataByDate");
      // Add a safety timeout to ensure loading state doesn't get stuck
      const safetyTimeout = setTimeout(() => {
        console.log("Safety timeout triggered - setting isLoading to false");
        setIsLoading(false);
      }, 10000); // 10 seconds timeout
      
      fetchDataByDate();
      
      // Cleanup timeout if component unmounts
      return () => clearTimeout(safetyTimeout);
    } else {
      console.log("useEffect: effective_standard mode, calling checkExisted");
      // For effective_standard, use existing logic
      const checkExisted = async () => {
        try {
          setError(null);
          
          const response = await api.get(getIndirect);
          
          if (response.data && response.status === 200) {
            const effectiveStandardData = response.data;
            
            if (effectiveStandardData && typeof effectiveStandardData === 'object' && !effectiveStandardData.message) {
              setData({
                custom_date: new Date(),
                cointa: effectiveStandardData.cointa || "",
                price_by_part: effectiveStandardData.price_by_part || "",
                mag_four: effectiveStandardData.mag_four || "",
                mag_fin: effectiveStandardData.mag_fin || "",
                autres: effectiveStandardData.autres || "",
                autres_detail: "",
                machines_sp_manuelle: effectiveStandardData.machines_sp_manuelle || "",
                trassage_special: effectiveStandardData.trassage_special || "",
                cont_fin: effectiveStandardData.cont_fin || "",
                mach_retouche: effectiveStandardData.mach_retouche || "",
                repassage: effectiveStandardData.repassage || "",
                gabaret: effectiveStandardData.gabaret || "",
                preparation_stagieres: effectiveStandardData.preparation_stagieres || "",
                preparation: effectiveStandardData.preparation || "",
                matlasseurs: effectiveStandardData.coupes?.[0]?.matlasseurs || "",
                coupeurs: effectiveStandardData.coupes?.[0]?.coupeurs || "",
                tiquitage: effectiveStandardData.coupes?.[0]?.tiquitage || "",
                vesline: effectiveStandardData.coupes?.[0]?.vesline || "",
              });

              setEffectiveStandardId(effectiveStandardData.id);
              calculateTotal(effectiveStandardData);
            } else {
              clearFormFields();
            }
          }
        } catch (error) {
          console.error("Error fetching effective indirect data:", error);
          clearFormFields();
        } finally {
          console.log("useEffect: checkExisted completed, setting isLoading to false");
          setIsLoading(false);
        }
      };

      checkExisted();
    }
  }, [url]);

  // Debug useEffect to monitor isLoading state changes
  useEffect(() => {
    console.log("🔄 isLoading state changed to:", isLoading);
  }, [isLoading]);

  // Function to refresh data when date changes
  const handleDateChange = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);
    
    // If this is the custom_date change and we're in effective_real mode,
    // automatically fetch data for the new date immediately
    if (url === "effective_real" && !isFetching) {
      // Create updated data object with the new date
      const updatedData = {
        ...data,
        custom_date: adjustedDate,
      };
      
      // Fetch data immediately with the updated data
      fetchDataByDate(updatedData);
    }
    
    // Update the state after the fetch logic
    setData({
      ...data,
      custom_date: adjustedDate,
    });
  };

  // Function to verify if the record still exists
  const verifyRecordExists = async () => {
    if (!effectiveStandardId || url !== "effective_standard") return true;
    
    try {
      await api.get(`/effective_standard/${effectiveStandardId}`);
      return true;
    } catch (error) {
      console.log("Record verification failed, clearing ID:", error);
      setEffectiveStandardId(null);
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (readOnly) return;

    if (url === "effective_real" && Number(data.autres) > 0 && !data.autres_detail.trim()) {
      toast.error("Le détail est obligatoire lorsque Autres est supérieur à zéro.");
      return;
    }

    setLoading(true);

    // Verify record exists before proceeding
    const recordExists = await verifyRecordExists();

    const formattedData = {
      cointa: data.cointa,
      price_by_part: typeof data.price_by_part === "number" ? data.price_by_part : "",
      effectif_indirects: [
        {
          mag_four: typeof data.mag_four === "number" ? data.mag_four : "",
          mag_fin: typeof data.mag_fin === "number" ? data.mag_fin : "",
          autres: typeof data.autres === "number" ? data.autres : "",
          ...(url === "effective_real" ? { autres_detail: data.autres_detail.trim() } : {}),
          machines_sp_manuelle: typeof data.machines_sp_manuelle === "number" ? data.machines_sp_manuelle : "",
          trassage_special: typeof data.trassage_special === "number" ? data.trassage_special : "",
          cont_fin: typeof data.cont_fin === "number" ? data.cont_fin : "",
          mach_retouche: typeof data.mach_retouche === "number" ? data.mach_retouche : "",
          repassage: typeof data.repassage === "number" ? data.repassage : "",
          gabaret: typeof data.gabaret === "number" ? data.gabaret : "",
          preparation_stagieres: typeof data.preparation_stagieres === "number" ? data.preparation_stagieres : "",
          preparation: typeof data.preparation === "number" ? data.preparation : "",
          coupes: [
            {
              matlasseurs: typeof data.matlasseurs === "number" ? data.matlasseurs : "",
              coupeurs: typeof data.coupeurs === "number" ? data.coupeurs : "",
              tiquitage: typeof data.tiquitage === "number" ? data.tiquitage : "",
              vesline: typeof data.vesline === "number" ? data.vesline : "",
            },
          ],
        },
      ],
    };

    // Add custom_date for effective_real
    if (url === "effective_real" && data.custom_date) {
      formattedData.custom_date = data.custom_date.toISOString().split("T")[0];
    }

    try {
      if (effectiveStandardId && recordExists) {
        // Update existing record
        const response = await api.post(`/${url}/${effectiveStandardId}`, formattedData);
        toast.success("Données mises à jour avec succès.");
      } else {
        // Insert new record (either no ID or record doesn't exist)
        let response;
        if (url === "effective_real") {
          // Use the new endpoint for storing indirect effectif by date
          response = await api.post(`/effective_indirect_real_by_date`, formattedData);
        } else {
          // Use the standard endpoint for effective_standard
          response = await api.post(`/${url}`, formattedData);
        }
        toast.success("Données enregistrées avec succès.");
      }

      
      // After successful save, clear form but preserve the date
      if (url === "effective_real") {
        // Clear form fields but preserve date
        clearFormFields();
      } else {
        // For effective_standard, clear everything including date
        const clearedData = {
          custom_date: new Date(), // Reset to current date
          cointa: "",
          price_by_part: "",
          mag_four: "",
          mag_fin: "",
          autres: "",
          autres_detail: "",
          machines_sp_manuelle: "",
          trassage_special: "",
          cont_fin: "",
          mach_retouche: "",
          repassage: "",
          gabaret: "",
          preparation_stagieres: "",
          preparation: "",
          matlasseurs: "",
          coupeurs: "",
          tiquitage: "",
          vesline: "",
        };
        
        setData(clearedData);
      }
      setEffectiveStandardId(null);
      setTotal(0);
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Erreur lors de l'enregistrement des données.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (readOnly) return;
    try {
      if (url === "effective_real") {
        // For effective_real, delete the EffectiveReal record
        await api.delete(`/effective_real/${effectiveStandardId}`);
      } else {
        // For effective_standard, use the standard endpoint
        await api.delete(`/${url}/${effectiveStandardId}`);
      }
      toast.success("Données supprimées avec succès.");
      
      // Clear form but preserve the date
      if (url === "effective_real") {
        clearFormFields();
      } else {
        // For effective_standard, clear everything including date
        const clearedData = {
          custom_date: new Date(), // Reset to current date
          cointa: "",
          price_by_part: "",
          mag_four: "",
          mag_fin: "",
          autres: "",
          autres_detail: "",
          machines_sp_manuelle: "",
          trassage_special: "",
          cont_fin: "",
          mach_retouche: "",
          repassage: "",
          gabaret: "",
          preparation_stagieres: "",
          preparation: "",
          matlasseurs: "",
          coupeurs: "",
          tiquitage: "",
          vesline: "",
        };
        
        setData(clearedData);
      }
      setEffectiveStandardId(null);
      setTotal(0);
    } catch (error) {
      toast.error("Erreur lors de la suppression des données.");
    }
  };

  return (
    <div className="ml-[16.66%] mr-5 pt-[6rem]">
      <ToastContainer />
      {isLoading ? (
        <div className="text-center text-2xl">Chargement...</div>
      ) : error ? (
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">{error}</div>
          <Button 
            classes="bg-blue-500" 
            onClick={() => {
              setIsLoading(true);
              setError(null);
              // Retry the data fetch
              setTimeout(() => {
                if (url === "effective_real") {
                  fetchDataByDate();
                } else {
                  // Retry logic for effective_standard
                  const checkExisted = async () => {
                    try {
                      setError(null);
                      const response = await api.get(getIndirect);
                      
                      if (response.data && response.status === 200) {
                        const effectiveStandardData = response.data;
                        
                        if (effectiveStandardData && typeof effectiveStandardData === 'object' && !effectiveStandardData.message) {
                          setData({
                            custom_date: new Date(),
                            cointa: effectiveStandardData.cointa || "",
                            price_by_part: effectiveStandardData.price_by_part || "",
                            mag_four: effectiveStandardData.mag_four || "",
                            mag_fin: effectiveStandardData.mag_fin || "",
                            autres: effectiveStandardData.autres || "",
                            autres_detail: "",
                            machines_sp_manuelle: effectiveStandardData.machines_sp_manuelle || "",
                            trassage_special: effectiveStandardData.trassage_special || "",
                            cont_fin: effectiveStandardData.cont_fin || "",
                            mach_retouche: effectiveStandardData.mach_retouche || "",
                            repassage: effectiveStandardData.repassage || "",
                            gabaret: effectiveStandardData.gabaret || "",
                            preparation_stagieres: effectiveStandardData.preparation_stagieres || "",
                            preparation: effectiveStandardData.preparation || "",
                            matlasseurs: effectiveStandardData.coupes?.[0]?.matlasseurs || "",
                            coupeurs: effectiveStandardData.coupes?.[0]?.coupeurs || "",
                            tiquitage: effectiveStandardData.coupes?.[0]?.tiquitage || "",
                            vesline: effectiveStandardData.coupes?.[0]?.vesline || "",
                          });

                          setEffectiveStandardId(effectiveStandardData.id);
                          calculateTotal(effectiveStandardData);
                        } else {
                          clearFormFields();
                        }
                      }
                    } catch (error) {
                      console.error("Error retrying fetch:", error);
                      setError("Erreur lors du rechargement. Veuillez réessayer.");
                    } finally {
                      setIsLoading(false);
                    }
                  };
                  checkExisted();
                }
              }, 100);
            }}
          >
            Réessayer
          </Button>
        </div>
      ) : (
        <form className="ml-7" onSubmit={handleSubmit}>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold my-5 mb-[3rem]">
              {url === "effective_real"
                ? "Effectif Réel Indirect"
                : "Effectif Standard Indirect"}
              <span className="text-xl font-normal">- Total: {total}</span>
            </h2>
            {!readOnly && effectiveStandardId && (
              <Button classes="bg-red-500 my-5 mb-8" onClick={handleDelete}>
                {url === "effective_real" && data.custom_date 
                  ? `Supprimer l'Effectif Indirect du ${data.custom_date.toLocaleDateString('fr-FR')}`
                  : "Supprimer l'Effectif Indirect"
                }
              </Button>
            )}
          </div>
          
          {/* Date Filter Section */}
          {url === "effective_real" && (
            <div className="mb-4">
              <label className="block font-semibold">Date de saisie des données</label>
              <FrenchDatePicker
                selected={data.custom_date}
                onChange={(date) => handleDateChange(date)}
                dateFormat="yyyy-MM-dd"
                className="outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded mt-4"
                placeholderText="Sélectionner une date"
              />
              <small className="text-gray-500 mt-1">
                {readOnly ? "Sélectionnez la date des données à consulter." : "Cette date sera utilisée pour stocker les données (par défaut: aujourd'hui)"}
              </small>
            </div>              
          )}
          
          {!effectiveStandardId && url === "effective_real" && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800">
                {`Aucun effectif indirect trouvé pour le ${data.custom_date.toLocaleDateString('fr-FR')}.`}
                {!readOnly && " Vous pouvez créer un nouvel effectif pour cette date en remplissant le formulaire ci-dessous."}
              </p>
            </div>
          )}

          {!effectiveStandardId && url === "effective_standard" && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800">
                Aucun effectif indirect trouvé. Vous pouvez créer un nouvel effectif en remplissant le formulaire ci-dessous.
              </p>
            </div>
          )}

          <fieldset disabled={readOnly}>
          <Input
            container="mb-4"
            label="Magasin fournitures"
            type="number"
            name="mag_four"
            text={data.mag_four}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Dépôt final"
            type="number"
            name="mag_fin"
            text={data.mag_fin}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Machines spéciales"
            type="number"
            name="machines_sp_manuelle"
            text={data.machines_sp_manuelle}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Trassage spécial"
            type="number"
            name="trassage_special"
            text={data.trassage_special}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Contrôle Final"
            type="number"
            name="cont_fin"
            text={data.cont_fin}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Machines Retouche"
            type="number"
            name="mach_retouche"
            text={data.mach_retouche}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Repassage Final"
            type="number"
            name="repassage"
            text={data.repassage}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Gabaret"
            type="number"
            name="gabaret"
            text={data.gabaret}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Préparation Stagiaires"
            type="number"
            name="preparation_stagieres"
            text={data.preparation_stagieres}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Préparation"
            type="number"
            name="preparation"
            text={data.preparation}
            handleChange={onChange}
          />


          <Input
            container="mb-4"
            label="Matlasseurs"
            type="number"
            name="matlasseurs"
            text={data.matlasseurs}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Coupeurs"
            type="number"
            name="coupeurs"
            text={data.coupeurs}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Tiquitage"
            type="number"
            name="tiquitage"
            text={data.tiquitage}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Vesline"
            type="number"
            name="vesline"
            text={data.vesline}
            handleChange={onChange}
          />

          <Input
            container="mb-4"
            label="Autres"
            type="number"
            name="autres"
            text={data.autres}
            handleChange={onChange}
          />

          {url === "effective_real" && (
            <div className="mb-4">
              <label className="block font-semibold" htmlFor="autres_detail">
                Détail {Number(data.autres) > 0 && <span className="text-red-600">*</span>}
              </label>
              <textarea
                id="autres_detail"
                name="autres_detail"
                rows={4}
                value={data.autres_detail}
                onChange={(event) => onChange("autres_detail", event.target.value)}
                required={Number(data.autres) > 0}
                maxLength={1000}
                placeholder="Précisez les autres effectifs"
                className="block mt-4 w-full min-w-[300px] rounded border border-[#b3b3b3] p-[.5rem] outline-0 focus:border-2 focus:border-[#2684ff]"
              />
            </div>
          )}
          
          <div className="mb-4">
            <label className="block font-semibold">Quenta</label>
            <div className="mt-4">
              <label className="mr-4">
                <input
                  type="radio"
                  name="cointa"
                  value={true}
                  checked={data.cointa === true}
                  onChange={(e) =>
                    onChange("cointa", e.target.value === "true")
                  }
                />
                Oui
              </label>
              <label className="mr-4">
                <input
                  type="radio"
                  name="cointa"
                  value={false}
                  checked={data.cointa === false}
                  onChange={(e) => onChange("cointa", e.target.value === 0)}
                />
                Non
              </label>
            </div>
          </div>

          {data.cointa && (
            <Input
              container="mb-4"
              label="Prix par pièce"
              type="number"
              name="price_by_part"
              text={data.price_by_part}
              handleChange={onChange}
            />
          )}

          </fieldset>
          {!readOnly && <Button classes="bg-blue-500 my-5 mb-8">
            {loading ? "Enregistrement ..." : 
              url === "effective_real" && effectiveStandardId 
                ? "Mettre à jour Effectif Indirect" 
                : "Enregistrer Effectif Indirect"
            }
          </Button>}

          {/* Clear Form button for effective_real */}
          {!readOnly && url === "effective_real" && (
            <Button classes="bg-gray-500 mb-8 mr-4" onClick={clearEntireForm}>
              Effacer le formulaire
            </Button>
          )}
          
          {/* Helper text for date-filtered mode */}
          {!readOnly && url === "effective_real" && (
            <div className="mt-2 text-sm text-gray-600 text-center">
              {effectiveStandardId ? (
                <span className="text-green-600">
                  💡 Vous modifiez les données existantes du {data.custom_date.toLocaleDateString('fr-FR')}
                </span>
              ) : (
                <span className="text-blue-600">
                  💡 Vous créez de nouvelles données pour le {data.custom_date.toLocaleDateString('fr-FR')}
                </span>
              )}
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default EffectiveIndirect;
